import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL10_BODY_LAYOUT_FIX = Object.freeze({
  firstPlatform: { x: -1735.9, y: -23.95 },
  barierPlatform: { x: -1555.0, y: -23.95 },
  firstBarier: { x: -1488.9, y: -54.0 },
  secondBarier: { x: -1335.9, y: -74.0 },
  thirdBarier: { x: -1183.45, y: -94.0 },
  forthBarier: { x: -1034.5, y: -114.0 },
  mainRampa: { x: 576.05, y: -488.0 },
});

const LEVEL10_DYNAMIC_SYMBOLS = Object.freeze({
  jumpPlatform1: {
    imagePath: "port/generated/symbols/184/png/1.png",
    width: 428.1,
    height: 309.15,
    offsetX: -173.1,
    offsetY: -151.85,
  },
  jumpPlatform2: {
    imagePath: "port/generated/symbols/184/png/1.png",
    width: 428.1,
    height: 309.15,
    offsetX: -173.1,
    offsetY: -151.85,
  },
  jumpPlatform3: {
    imagePath: "port/generated/symbols/184/png/1.png",
    width: 428.1,
    height: 309.15,
    offsetX: -173.1,
    offsetY: -151.85,
  },
  cube1: {
    imagePath: "port/generated/symbols/194/png/1.png",
    width: 294.15,
    height: 294.15,
    offsetX: -169.1,
    offsetY: -167.75,
  },
  cube2: {
    imagePath: "port/generated/symbols/194/png/1.png",
    width: 294.15,
    height: 294.15,
    offsetX: -169.1,
    offsetY: -167.75,
  },
  cube3: {
    imagePath: "port/generated/symbols/194/png/1.png",
    width: 294.15,
    height: 294.15,
    offsetX: -169.1,
    offsetY: -167.75,
  },
  goBall: {
    imagePath: "port/generated/symbols/201/png/1.png",
    width: 408.55,
    height: 233.2,
    offsetX: -111.9,
    offsetY: -85.6,
  },
  roundBlock: {
    imagePath: "port/generated/symbols/203/png/1.png",
    width: 128.3,
    height: 33.0,
    offsetX: -0.5,
    offsetY: -0.45,
  },
});

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelTenRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_10/1.visual.runtime.svg";
      this.level10DynamicImages = Object.fromEntries(
        Object.entries(LEVEL10_DYNAMIC_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
    } else {
      this.level10DynamicImages = {};
    }

    for (const [name, fix] of Object.entries(LEVEL10_BODY_LAYOUT_FIX)) {
      const layout = this.levelData.layout.namedChildren[name];
      if (layout) {
        layout.x = fix.x;
        layout.y = fix.y;
      }
    }

    this.createBody("firstPlatform", "Polygon", 0, 1, 0.2, [[[112, 0], [112, 20], [0, 20], [0, 0]]]);
    this.createBody("barierPlatform", "Polygon", 0, 1, 0.2, [[[610, 0], [610, 20], [0, 20], [0, 0]]]);
    this.createBody("firstBarier", "Polygon", 0, 1, 0.2, [[[23.5, 0], [20, 3.55], [0, 3.55], [-3.75, 0]], [[0, 3.55], [20, 3.55], [20, 30], [0, 30]]]);
    this.createBody("secondBarier", "Polygon", 0, 1, 0.2, [[[23.5, 0], [20, 4], [0, 3.75], [-3.5, 0]], [[0, 3.75], [20, 4], [20, 50], [0, 50]]]);
    this.createBody("thirdBarier", "Polygon", 0, 1, 0.2, [[[23.5, 0], [20, 3.75], [0, 4], [-3.75, 0]], [[0, 4], [20, 3.75], [20, 70], [0, 70]]]);
    this.createBody("forthBarier", "Polygon", 0, 1, 0.2, [[[27.25, 0], [23.75, 4.8], [3.75, 5.15], [0, 0]], [[3.75, 5.15], [23.75, 4.8], [23.75, 90], [3.75, 90]]]);
    this.jumpPlatform1Body = this.createBody("jumpPlatform1", "Polygon", 1, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.jumpPlatform2Body = this.createBody("jumpPlatform2", "Polygon", 1, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.jumpPlatform3Body = this.createBody("jumpPlatform3", "Polygon", 1, 1, 0.2, [[[100, 0], [100, 20], [0, 20], [0, 0]]]);
    this.createBody("afterJump", "Polygon", 0, 1, 0.2, [[[301.45, -0.25], [372.45, -0.25], [301.45, 20.25], [232.95, 30]], [[71, 0], [138.95, 30], [71, 20.5], [0, 0]], [[0, 20.5], [0, 0], [71, 20.5]], [[71, 20.5], [138.95, 30], [139.45, 50]], [[138.95, 30], [232.95, 30], [232.95, 50], [139.45, 50]], [[232.95, 50], [232.95, 30], [301.45, 20.25]], [[372.45, 20.25], [301.45, 20.25], [372.45, -0.25]]]);
    this.cube1Body = this.createBody("cube1", "Polygon", 1, 1, 0.2, [[[-50, 50], [-50, -50], [50, -50], [50, 50]]]);
    this.cube2Body = this.createBody("cube2", "Polygon", 1, 1, 0.2, [[[-50, 50], [-50, -50], [50, -50], [50, 50]]]);
    this.cube3Body = this.createBody("cube3", "Polygon", 1, 1, 0.2, [[[-50, 50], [-50, -50], [50, -50], [50, 50]]]);
    this.createBody("mainRampa", "Polygon", 0, 1, 0.2, this.levelData.bodies.find((body) => body.name === "mainRampa").data);
    this.createBody("goBall", "Circle", 0.5, 1, 0.2, [80]);
    this.roundBlockBody = this.createBody("roundBlock", "Polygon", 1, 1, 0.2, [[[127.3, 26.5], [122, 33.3], [107, 20.8], [109.7, 12.85]], [[84.2, 2.4], [109.7, 12.85], [82, 10.3], [54, 0]], [[24.95, 6.5], [54, 0], [27.5, 13.8], [0, 21.7]], [[3.75, 28.05], [0, 21.7], [27.5, 13.8]], [[27.5, 13.8], [54, 0], [54.5, 8.3]], [[54.5, 8.3], [54, 0], [82, 10.3]], [[82, 10.3], [109.7, 12.85], [107, 20.8]]]);

    this.jumpPlatform1Joint = this.addPrismaticJoint(this.jumpPlatform1Body, this.ground, this.jumpPlatform1Body.GetWorldCenter(), new b2Vec2(0, 1), { lowerTranslation: 0, upperTranslation: 1, enableLimit: true, maxMotorForce: 0, motorSpeed: 200, enableMotor: true });
    this.jumpPlatform2Joint = this.addPrismaticJoint(this.jumpPlatform2Body, this.ground, this.jumpPlatform2Body.GetWorldCenter(), new b2Vec2(0, 1), { lowerTranslation: 0, upperTranslation: 1, enableLimit: true, maxMotorForce: 0, motorSpeed: 200, enableMotor: true });
    this.jumpPlatform3Joint = this.addPrismaticJoint(this.jumpPlatform3Body, this.ground, this.jumpPlatform3Body.GetWorldCenter(), new b2Vec2(0, 1), { lowerTranslation: 0, upperTranslation: 1, enableLimit: true, maxMotorForce: 0, motorSpeed: 200, enableMotor: true });
    this.addRevoluteJoint(this.cube1Body, this.ground, this.cube1Body.GetPosition(), { motorSpeed: 0.3 * Math.PI, maxMotorTorque: 5000, enableMotor: true });
    this.addRevoluteJoint(this.cube2Body, this.ground, this.cube2Body.GetPosition(), { motorSpeed: -0.3 * Math.PI, maxMotorTorque: 5000, enableMotor: true });
    this.addRevoluteJoint(this.cube3Body, this.ground, this.cube3Body.GetPosition(), { motorSpeed: 0.3 * Math.PI, maxMotorTorque: 5000, enableMotor: true });
    this.addRevoluteJoint(this.roundBlockBody, this.ground, new b2Vec2(-675 / 30, -152 / 30), { motorSpeed: -0.9 * Math.PI, maxMotorTorque: 5000, enableMotor: true });
  }

  levelUpdate() {
    if (this.player.y > 500 || this.contactListener.playerContactBodies.indexOf(this.roundBlockBody) !== -1) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    this.jumpPlatform1Joint.SetMaxMotorForce(this.contactListener.playerContactBodies.indexOf(this.jumpPlatform1Body) !== -1 ? 150 : 0);
    this.jumpPlatform2Joint.SetMaxMotorForce(this.contactListener.playerContactBodies.indexOf(this.jumpPlatform2Body) !== -1 ? 150 : 0);
    this.jumpPlatform3Joint.SetMaxMotorForce(this.contactListener.playerContactBodies.indexOf(this.jumpPlatform3Body) !== -1 ? 150 : 0);
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    for (const name of ["jumpPlatform1", "jumpPlatform2", "jumpPlatform3", "cube1", "cube2", "cube3", "goBall", "roundBlock"]) {
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawSymbolImage(
        ctx,
        this.level10DynamicImages[name],
        LEVEL10_DYNAMIC_SYMBOLS[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
